import React, {
  useEffect,
  useState,
  PropsWithChildren,
  useCallback,
} from 'react';
import { User } from '@supabase/supabase-js';
import { AuthContext, MyContext } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { UserRecord } from '../model/types';

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userRecord, setUserRecord] = useState<UserRecord | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const clearAuthError = useCallback(() => setAuthError(null), []);

  const checkEmailVerification = useCallback(async (): Promise<boolean> => {
    const { data } = await supabase.auth.getUser();
    const verified = data.user?.email_confirmed_at != null;
    setIsEmailVerified(verified);
    return verified;
  }, []);

  const getNotifications = useCallback(() => undefined, []);
  const getFollows = useCallback(() => undefined, []);

  useEffect(() => {
    setIsLoading(true);

    // Get the initial session synchronously
    supabase.auth.getSession().then(({ data: { session } }) => {
      const supabaseUser = session?.user ?? null;
      setUser(supabaseUser);
      setIsEmailVerified(supabaseUser?.email_confirmed_at != null);
      if (!supabaseUser) setIsLoading(false);
    });

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const supabaseUser = session?.user ?? null;
        setUser(supabaseUser);
        setIsEmailVerified(supabaseUser?.email_confirmed_at != null);
        if (!supabaseUser) {
          setUserRecord(null);
          setIsLoading(false);
        }
      },
    );

    return () => subscription.unsubscribe();
  }, []);

  // Fetch user record when auth user changes
  useEffect(() => {
    if (!user) return;

    setIsLoading(true);

    supabase
      .from('users')
      .select('*')
      .eq('auth_id', user.id)
      .single()
      .then(({ data, error }) => {
        if (error) {
          // User record may not exist yet if DB trigger hasn't fired
          console.warn('User record not found:', error.message);
          setIsLoading(false);
          return;
        }
        setUserRecord(data as UserRecord);
        setIsLoading(false);
      });

    // Subscribe to realtime updates on this user's row
    const channel = supabase
      .channel(`user-record-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'users',
          filter: `auth_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setUserRecord(null);
          } else {
            setUserRecord(payload.new as UserRecord);
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={
        {
          user,
          userRecord,
          isEmailVerified,
          isLoading,
          authError,
          clearAuthError,
          getNotifications,
          getFollows,
          checkEmailVerification,
        } as MyContext
      }
    >
      {children}
    </AuthContext.Provider>
  );
};
